<template>
	<div :class="['review', { compact }]">
		<div class="review-header">
			<ximg
				:src="avatarUrl"
				:class="['pixelated avatar', { 'avatar-small': ! review.text || compact }]"
				:style="avatarStyle"
				eager
				ariaLabel="avatar"
				role="img"
			/>
			<div class="review-info">
				<div class="score-time">
					<stars
						v-if="! review.isHype"
						:stars="review.rating"
					/>
					<flames
						v-else
						:flames="review.rating"
					/>
					<span class="sub-title">{{ postedString }}</span>
				</div>
				<span class="user">
					<router-link
						v-if="review.user && review.user.isPublicProfile && ! review.isAnonymous"
						:to="userPath"
						class="name"
					>
						{{ review.user.username }}
					</router-link>
					<span v-else class="name">
						{{ review.user && ! review.isAnonymous ? review.user.username : 'No-Face' }}
					</span>
					<knighthood-icon v-if="review.user && review.user.knighthood > 0"  />
					<template v-if="review.verifiedPurchase && shouldShowIfVerified">
						<template v-if="! review.isHype">
							<span
								:title="tr('VERIFIED_PURCHASE')"
								class="review-verified purchase"
							/>
							<span
								v-if="! review.isEmployee"
								class="icon-description"
							>
								{{ tr('VERIFIED_PURCHASE') }}
							</span>
						</template>
						<template v-else-if="review.isHype">
							<span
								:title="tr('VERIFIED_PREPURCHASE')"
								class="review-verified hype"
							/>
							<span
								v-if="! review.isEmployee"
								class="icon-description"
							>
								{{ tr('VERIFIED_PREPURCHASE') }}
							</span>
						</template>
					</template>
					<template v-if="review.isEmployee">
						<span
							title="Jobbar på Webhallen"
							class="wh-logo"
						/>
						<span class="icon-description">Jobbar på webhallen</span>
					</template>
				</span>
				<span
					v-if="review.text && ! compact && review.user"
					class="user-level"
				>
					Lvl {{ review.user.rankLevel }} {{ review.user.avatar.class.title }}
				</span>
				<div
					v-if="(! compact && review.text) || adminButtons"
					class="votes"
				>
					<template v-if="! compact && review.text">
						<span
							:title="tr('VOTE_UP')"
							:class="['vote vote-up secondary', { active: usersVote > 0 }]"
							@click="! adminButtons ? vote(1) : null"
						>{{ review.upvotes }}</span>
						<span
							:title="tr('VOTE_DOWN')"
							:class="['vote vote-down secondary', { active: usersVote < 0 }]"
							@click="! adminButtons ? vote(-1) : null"
						>{{ review.downvotes }}</span>
					</template>
					<text-button
						v-if="showDeleteButton && adminButtons"
						class="ml-4"
						text="Ta bort"
						size="tiny"
						width="narrow"
						variant="destructive"
						@click="$emit('delete')"
					/>
				</div>
			</div>
		</div>
		<div
			v-if="review.text"
			:class="['review-text', { 'compact-review-text': abTest.shorterReviews > 0 && ! isExpanded }]"
		>
			<template v-if="compact">
				<fixed-lines
					:lines="2"
					:bleedHeight="0.2"
				>
					<p
						v-for="(row, index) in review.text.split('\n')"
						:key="'review-text-' + index"
					>
						{{ row }}
					</p>
				</fixed-lines>
			</template>
			<template v-else>
				<p
					v-for="(row, index) in review.text.split('\n')"
					:key="'review-text-' + index"
				>
					{{ row }}
				</p>
			</template>
		</div>
		<a
			v-if="abTest.shorterReviews > 0 && (review.text && numberOfRows > 7 || numberOfSymbols > 300)"
			@click="isExpanded = ! isExpanded"
			class="read-more"
		>
			{{ isExpanded ? 'Visa mindre' : 'Visa mer' }}
		</a>

		<span
			v-if="displayProductName && review.product"
			class="sub-title mt-2"
		>
			{{ review.product.name }}
		</span>
		<hr
			v-if="! compact"
			:class="{'_strong' : isUpvotedReview}"
		>
	</div>
</template>

<script>
import Vue from 'vue';
import waja from 'waja';
import { t } from 'datastore/i18n';
import alert from 'alert';
import store from 'datastore';
import distanceInWordsToNow from 'date-fns/distance_in_words_to_now';
import svLocale from 'date-fns/locale/sv';
import memberUtils from 'utils/member';
import { mapState, mapGetters } from 'vuex';
import { canUserViewProductInSecretMode, isProductInSecretMode, memberRoute } from 'utils';
import fixedLines from './fixed-lines.vue';
import abTest from 'abTest';

export default {
	components: { fixedLines },
	store,
	props: {
		adminButtons: {
			type: Boolean,
			default: false,
		},
		review: {
			type: Object,
			default: null,
		},
		userVotes: {
			type: [Array, Object],
			default: null,
		},
		displayProductName: {
			type: Boolean,
			default: false,
		},
		showDeleteButton: {
			type: Boolean,
			default: false,
		},
		compact: {
			type: Boolean,
			default: false,
		},
		isUpvotedReview: {
			type: Boolean,
			default: null,
		},
	},
	data () {
		return {
			isUpvoted: false,
			isDownvoted: false,
			isExpanded: false,
			abTest,
		};
	},
	computed: {
		shouldShowIfVerified () {
			if (isProductInSecretMode(this.review.product)) {
				if (! canUserViewProductInSecretMode(this.user, this.review.product)) {
					return false;
				}
			}
			return true;
		},
		userPath () {
			return this.$router.route(memberRoute(this.review.user, 'member.achievements'));
		},
		avatarStyle () {
			return memberUtils.avatarStyle(this.review);
		},
		avatarUrl () {
			return memberUtils.avatarUrl(this.review);
		},
		postedString () {
			this.languageCode;
			const string = distanceInWordsToNow(this.review.createdAt * 1000, {
				locale: svLocale,
				addSuffix: true,
				onlyNumeric: true,
			});
			return string;
		},
		usersVote: {
			get () {
				return this.userVotes[this.review.id] || 0;
			},
			set (val) {
				Vue.set(this.userVotes, this.review.id, val);
			},
		},
		numberOfRows () {
			return this.review.text.split('\n').length;
		},
		numberOfSymbols () {
			return this.review.text.length;
		},
		...mapState({
			user: state => state.user,
		}),
		...mapGetters({
			isLoggedIn: 'user/isLoggedIn',
			languageCode: 'i18n/languageCode',
			isDark: 'user/isDark',
		}),
	},
	methods: {
		tr: t.prefix('PRODUCT.REVIEW'),
		vote (newVote) {
			if (! this.isLoggedIn) {
				alert('Endast medlemmar kan rösta!');
				return;
			}
			if (this.usersVote === 1) {
				this.review.upvotes--;
			} else if (this.usersVote === -1) {
				this.review.downvotes--;
			}
			if (this.usersVote === newVote) {
				newVote = 0;
			}
			this.usersVote = newVote;
			if (newVote === 1) {
				this.review.upvotes++;
			} else if (newVote === -1) {
				this.review.downvotes++;
			}
			waja.put('review/' + this.review.id + '/vote')
				.data({
					vote: newVote,
				})
				.on('success', () => {
					alert('Tack för din röst!');
				})
				.go();
			// @TODO error handler
		},
	},
};
</script>